import { useState } from 'react';
import publicTripService from '../../../services/public-trip.service';

export interface PublishTripSettings {
  excludeNotes: boolean;
  isDiscoverable: boolean;
}

export function usePublishTrip() {
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publishTrip = async (
    tripId: string,
    settings: PublishTripSettings
  ): Promise<boolean> => {
    setIsPublishing(true);
    setError(null);

    try {
      await publicTripService.publishTrip({
        tripId,
        excludeNotes: settings.excludeNotes,
        isDiscoverable: settings.isDiscoverable
      });

      return true;
    } catch (err: any) {
      const errorMessage =
        err?.message || 'Failed to publish trip. Please try again.';
      setError(errorMessage);
      return false;
    } finally {
      setIsPublishing(false);
    }
  };

  const republishTrip = async (
    tripId: string,
    settings: PublishTripSettings
  ): Promise<boolean> => {
    setIsPublishing(true);
    setError(null);

    try {
      await publicTripService.republishTrip(tripId, {
        excludeNotes: settings.excludeNotes,
        isDiscoverable: settings.isDiscoverable
      });

      return true;
    } catch (err: any) {
      const errorMessage =
        err?.message || 'Failed to republish trip. Please try again.';
      setError(errorMessage);
      return false;
    } finally {
      setIsPublishing(false);
    }
  };

  const unpublishTrip = async (tripId: string): Promise<boolean> => {
    setIsPublishing(true);
    setError(null);

    try {
      await publicTripService.unpublishTrip(tripId);

      return true;
    } catch (err: any) {
      const errorMessage =
        err?.message || 'Failed to unpublish trip. Please try again.';
      setError(errorMessage);
      return false;
    } finally {
      setIsPublishing(false);
    }
  };

  const checkIfPublished = async (tripId: string): Promise<boolean> => {
    try {
      return await publicTripService.checkIfPublished(tripId);
    } catch (err: any) {
      console.error('Error checking if trip is published:', err);
      return false;
    }
  };

  return {
    publishTrip,
    republishTrip,
    unpublishTrip,
    checkIfPublished,
    isPublishing,
    error
  };
}
